/**
 * usePropertyAmenities — TanStack Query hook over
 * `GET /organizations/:slug/properties/:propertyId/amenities` (SCRUM-141 /
 * DS-010-5).
 *
 * The BE returns the amenities ALREADY grouped by category
 * (`PropertyAmenitiesListResponseDto = { groups }`). This hook applies only the
 * canonical GROUP ORDER (`orderGroupsByCategory` — known-by-taxonomy-index,
 * unknowns appended alphabetically) at READ via `React.useMemo`; the query
 * CACHE holds the RAW response so the set-mutation's optimistic write and
 * rollback operate on the untransformed server shape.
 *
 * Property-scoped (unlike the global catalog): gated on `property.amenity.read`
 * PLUS property-access, so it carries BOTH a 403 forbidden sentinel and a 404
 * not-found sentinel (cross-org anti-enum / stale URL). Mirrors
 * `use-property-detail.ts`.
 *
 * The result type is EXPORTED — step 7 re-exports it from the property barrel
 * for the booking sales-info consumer.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { propertyQueryKeys } from './queryKeys.js';
import { orderGroupsByCategory } from './amenities-mapper.js';

export type PropertyAmenityGroupDto =
  components['schemas']['PropertyAmenityGroupDto'];
type PropertyAmenitiesListResponseDto =
  components['schemas']['PropertyAmenitiesListResponseDto'];

/** 403 sentinel — the caller lost `property.amenity.read` since `/auth/me` cached. */
const FORBIDDEN = Symbol('property-amenities:forbidden');
/** 404 sentinel — property gone / cross-org (anti-enumeration). */
const NOT_FOUND = Symbol('property-amenities:not-found');

export type UsePropertyAmenitiesResult =
  | { readonly status: 'loading'; readonly data?: undefined }
  | { readonly status: 'ready'; readonly data: PropertyAmenityGroupDto[] }
  | { readonly status: 'forbidden'; readonly data?: undefined }
  | { readonly status: 'not-found'; readonly data?: undefined }
  | { readonly status: 'error'; readonly data?: undefined };

export function usePropertyAmenities(
  propertyId: string | null,
): UsePropertyAmenitiesResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const enabled =
    orgSlug !== null && propertyId != null && propertyId.length > 0;

  const query = useQuery({
    queryKey: [
      ...propertyQueryKeys.propertyAmenities(propertyId ?? ''),
      orgSlug,
    ] as const,
    enabled,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/properties/{propertyId}/amenities',
        {
          params: {
            path: { slug: orgSlug as string, propertyId: propertyId as string },
          },
        },
      );
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (result.response.status === 404) {
        return NOT_FOUND;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/properties/${propertyId}/amenities failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const data = React.useMemo<PropertyAmenityGroupDto[] | null>(() => {
    const raw = query.data;
    if (raw === undefined || raw === FORBIDDEN || raw === NOT_FOUND) {
      return null;
    }
    return orderGroupsByCategory(
      (raw as PropertyAmenitiesListResponseDto).groups,
    );
  }, [query.data]);

  if (!enabled || query.isPending) {
    return { status: 'loading' };
  }
  if (query.isError) {
    return { status: 'error' };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden' };
  }
  if (query.data === NOT_FOUND || data === null) {
    return { status: 'not-found' };
  }

  return { status: 'ready', data };
}
